/* Enchanted World — F10.E1
 * Site-wide canvas-based starfield + constellations + global dust.
 * Compositor-friendly: single <canvas> with manual RAF loop,
 * throttled to 30fps on mobile, 60fps on desktop.
 * Pauses on tab hidden (visibilitychange).
 * Reduced-motion: draws 1 static frame and stops.
 */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;

  function init() {
    var canvas = document.querySelector('.enchanted-world-canvas');
    if (!canvas) return;

    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    var dpr = Math.min(window.devicePixelRatio || 1, 2); // cap at 2× for perf
    var width = 0;
    var height = 0;

    function resize() {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();

    /* ---------- Read palette tokens from CSS vars ---------- */
    var rootStyles = getComputedStyle(document.documentElement);
    function readVar(name, fallback) {
      var v = rootStyles.getPropertyValue(name).trim();
      return v || fallback;
    }
    var STAR_CREAM = readVar('--ew-star-cream', '246, 241, 232');
    var STAR_GOLD = readVar('--ew-star-gold', '220, 180, 120');
    var CONSTELLATION = readVar('--ew-constellation', '184, 149, 119');
    var DUST_CORE = readVar('--ew-dust-core', '246, 241, 232');
    var DUST_HALO = readVar('--ew-dust-halo', '184, 149, 119');

    /* ---------- Generate stars (3-tier depth field for cosmic immersion) ----------
       bg  ~50% : tiny dim stars, slow shimmer (riempimento atmosferico)
       mid ~32% : medium stars con halo
       hero ~18%: stelle grosse stilizzate a 4 punte (✦) con spikes + halo doppio
       Densità alta + tre profondità = effetto universo, non puntini sparsi.
    */
    var STAR_COUNT = isMobile ? 60 : 110;
    var stars = [];
    for (var i = 0; i < STAR_COUNT; i++) {
      var rTier = Math.random();
      var tier, size, baseAlpha, twinkleAmp, depth;
      var lifespan;
      if (rTier < 0.50) {
        tier = 'bg';
        size = 0.6 + Math.random() * 0.7;            // 0.6–1.3
        baseAlpha = 0.30 + Math.random() * 0.25;     // 0.30–0.55
        depth = 0.04;
        lifespan = 1.8 + Math.random() * 1.6;        // 1.8–3.4 s (cicli rapidi)
      } else if (rTier < 0.82) {
        tier = 'mid';
        size = 1.4 + Math.random() * 1.1;            // 1.4–2.5
        baseAlpha = 0.55 + Math.random() * 0.30;     // 0.55–0.85
        depth = 0.22;
        lifespan = 2.8 + Math.random() * 2.4;        // 2.8–5.2 s
      } else {
        tier = 'hero';
        // Alcune hero (≈30%) sono "premium": più grandi e brillanti = molto evidenti
        var isPremium = Math.random() < 0.3;
        size = isPremium ? (3.2 + Math.random() * 1.5)    // 3.2–4.7
                         : (2.0 + Math.random() * 1.2);   // 2.0–3.2
        baseAlpha = isPremium ? 1.0 : (0.78 + Math.random() * 0.18);
        depth = 0.55;
        lifespan = 3.8 + Math.random() * 3.2;        // 3.8–7.0 s
      }
      stars.push({
        x: Math.random() * width,
        y: Math.random() * height,
        size: size,
        baseAlpha: baseAlpha,
        spawnTime: -Math.random() * 8,                // phase offset → alternanza naturale
        lifespan: lifespan,
        lastCycle: -1,
        flickerPhase: Math.random() * Math.PI * 2,
        gold: Math.random() > 0.7,
        tier: tier,
        depth: depth,
        inConstellation: -1
      });
    }

    /* ---------- Form constellations: 4 clusters desktop / 3 mobile ---------- */
    var CONSTELLATION_COUNT = isMobile ? 3 : 4;
    var constellations = [];
    var MAX_CLUSTER_RADIUS = isMobile ? 200 : 260;

    for (var c = 0; c < CONSTELLATION_COUNT; c++) {
      // Anchor random nel viewport (margini 12%)
      var anchorX = width * (0.12 + Math.random() * 0.76);
      var anchorY = height * (0.12 + Math.random() * 0.76);

      // Candidati: stelle mid/hero libere entro raggio (skip bg → costellazioni "vere")
      var candidates = [];
      for (var j = 0; j < stars.length; j++) {
        if (stars[j].inConstellation !== -1) continue;
        if (stars[j].tier === 'bg') continue;
        var dx = stars[j].x - anchorX;
        var dy = stars[j].y - anchorY;
        var d = Math.sqrt(dx * dx + dy * dy);
        if (d < MAX_CLUSTER_RADIUS) {
          candidates.push({ idx: j, d: d });
        }
      }
      candidates.sort(function (a, b) { return a.d - b.d; });
      var take = Math.min(candidates.length, 3 + Math.floor(Math.random() * 3)); // 3-5 stelle

      if (take >= 3) {
        var clusterIndices = [];
        for (var k = 0; k < take; k++) {
          stars[candidates[k].idx].inConstellation = c;
          clusterIndices.push(candidates[k].idx);
        }
        // Ordina per angolo dall'anchor per disegnare linea smooth
        clusterIndices.sort(function (ai, bi) {
          var aa = Math.atan2(stars[ai].y - anchorY, stars[ai].x - anchorX);
          var bb = Math.atan2(stars[bi].y - anchorY, stars[bi].x - anchorX);
          return aa - bb;
        });
        constellations.push(clusterIndices);
      }
    }

    /* ---------- Shooting stars (meteore: traccia gradient + testa brillante) ----------
       Compaiono random ogni 5–11 s desktop / 11–18 s mobile. Volo diagonale dall'alto
       o da sinistra-alto. Vita breve: ~1.4–2.0 s. Fanno "succedere qualcosa" nello
       sfondo → universo vivo, non statico. */
    var shootingStars = [];
    var nextShootingSpawn = performance.now() + (isMobile ? 4000 : 2500);
    function spawnShootingStar() {
      var fromTop = Math.random() > 0.45;
      var startX, startY, angleDeg;
      if (fromTop) {
        startX = Math.random() * width * 0.85 + width * 0.05;
        startY = -30;
        angleDeg = 35 + Math.random() * 30; // 35°–65° (verso basso-destra)
      } else {
        startX = -30;
        startY = Math.random() * height * 0.45;
        angleDeg = 18 + Math.random() * 22; // shallow, da sinistra
      }
      var rad = angleDeg * Math.PI / 180;
      var speed = 7.5 + Math.random() * 3.5; // px/frame @60fps
      shootingStars.push({
        x: startX, y: startY,
        vx: Math.cos(rad) * speed,
        vy: Math.sin(rad) * speed,
        trailLen: 100 + Math.random() * 70,
        life: 0,
        lifespan: 1400 + Math.random() * 600,
        gold: Math.random() > 0.5
      });
    }

    /* ---------- Magic beam (fascio brillantinoso multicolore non lineare) ----------
       Una "scia magica" attraversa la viewport seguendo una curva Bezier cubica
       con 2 control point random → percorso non lineare (arco/swing/zig-zag).
       La testa emette brillantini multicolore (magenta/cyan/gold/violet/pink/mint)
       che cadono dietro lasciando una scia colorata. Spawn periodico.
       --------------------------------------------------------------------------- */
    var BEAM_PALETTE = [
      '255, 100, 200', // magenta
      '120, 200, 255', // cyan
      '255, 200, 100', // gold
      '200, 130, 255', // violet
      '255, 130, 160', // pink
      '180, 255, 200', // mint
      '255, 230, 130'  // warm cream
    ];

    var beams = [];
    var beamSparkles = [];
    var nextBeamSpawn = performance.now() + 1500;

    /* Espone una API per spawn-are sparkle nel canvas dall'esterno (creature trail).
       initCreatures la usa per emettere scia dietro unicorno (rainbow) e drago (fuoco). */
    window.__ewEmitTrailParticle = function (opts) {
      if (!opts || typeof opts.x !== 'number') return;
      beamSparkles.push({
        x: opts.x,
        y: opts.y,
        vx: opts.vx || 0,
        vy: opts.vy || 0,
        size: opts.size || 1.5,
        color: opts.color || '255, 230, 140',
        life: 0,
        lifespan: opts.lifespan || 1000
      });
    };

    function pointOnEdge(edge) {
      if (edge === 0) return [Math.random() * width, -30];                  // top
      if (edge === 1) return [Math.random() * width, height + 30];          // bottom
      if (edge === 2) return [-30, Math.random() * height];                 // left
      return [width + 30, Math.random() * height];                          // right
    }

    function spawnBeam() {
      var startEdge = Math.floor(Math.random() * 4);
      var endEdge;
      do { endEdge = Math.floor(Math.random() * 4); } while (endEdge === startEdge);
      var s = pointOnEdge(startEdge);
      var e = pointOnEdge(endEdge);
      // 2 control point random nel viewport → curva non lineare
      var c1 = [width * (0.10 + Math.random() * 0.80), height * (0.10 + Math.random() * 0.80)];
      var c2 = [width * (0.10 + Math.random() * 0.80), height * (0.10 + Math.random() * 0.80)];
      beams.push({
        p0: s, p1: c1, p2: c2, p3: e,
        age: 0,
        lifespan: 4500 + Math.random() * 2500,  // 4.5–7s di volo
        lastEmit: 0,
        hueIndex: Math.floor(Math.random() * BEAM_PALETTE.length)
      });
    }

    function bezierPoint(b, t) {
      var u = 1 - t;
      var uu = u * u, uuu = uu * u;
      var tt = t * t, ttt = tt * t;
      return [
        uuu * b.p0[0] + 3 * uu * t * b.p1[0] + 3 * u * tt * b.p2[0] + ttt * b.p3[0],
        uuu * b.p0[1] + 3 * uu * t * b.p1[1] + 3 * u * tt * b.p2[1] + ttt * b.p3[1]
      ];
    }

    /* ---------- Click & hover sparkles (interazione brillantinosa) ----------
       Click ovunque → burst di 8-12 brillantini multicolore al punto.
       Hover su .button / .card-gallery / .product-card / link nav / heading
       → emissione lieve continua di brillantini intorno al bounding rect.
       Tutti vanno nello stesso buffer beamSparkles per render unificato.
       ------------------------------------------------------------------------ */
    function emitSparkleBurst(cx, cy, count, spread, speed) {
      for (var i = 0; i < count; i++) {
        var angle = Math.random() * Math.PI * 2;
        var dist = Math.random() * spread;
        var sp = (Math.random() * 0.8 + 0.2) * (speed || 0.6);
        beamSparkles.push({
          x: cx + Math.cos(angle) * dist * 0.3,
          y: cy + Math.sin(angle) * dist * 0.3,
          vx: Math.cos(angle) * sp,
          vy: Math.sin(angle) * sp - 0.15, // leggero drift verso l'alto
          size: 1.2 + Math.random() * 2.2,
          color: BEAM_PALETTE[Math.floor(Math.random() * BEAM_PALETTE.length)],
          life: 0,
          lifespan: 800 + Math.random() * 900
        });
      }
    }

    document.addEventListener('click', function (ev) {
      // skip click su input/select/button con focus → non spammare durante UX critica
      var tgt = ev.target;
      if (tgt && tgt.matches && tgt.matches('input, select, textarea, [contenteditable]')) return;
      emitSparkleBurst(ev.clientX, ev.clientY, isMobile ? 7 : 12, 18, 0.9);
    }, { passive: true });

    // Hover aura su elementi "selezionabili" — emette brillantini lievi continui
    var hoverEmitTimer = null;
    var hoveredEl = null;
    var HOVER_SELECTOR = '.button, .product-card, .card-gallery, .collection-card, ' +
                         '.header__menu-item, .header-menu__link, ' +
                         'h1, h2, h3, .h1, .h2, .h3';

    function startHoverEmit(el) {
      if (hoveredEl === el) return;
      stopHoverEmit();
      hoveredEl = el;
      hoverEmitTimer = window.setInterval(function () {
        if (!hoveredEl) return;
        var rect = hoveredEl.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return;
        // Spawn 1 brillantino lungo il perimetro (random edge point)
        var perim = (rect.width + rect.height) * 2;
        var p = Math.random() * perim;
        var x, y;
        if (p < rect.width) { x = rect.left + p; y = rect.top; }
        else if (p < rect.width + rect.height) { x = rect.right; y = rect.top + (p - rect.width); }
        else if (p < rect.width * 2 + rect.height) { x = rect.right - (p - rect.width - rect.height); y = rect.bottom; }
        else { x = rect.left; y = rect.bottom - (p - rect.width * 2 - rect.height); }
        beamSparkles.push({
          x: x, y: y,
          vx: (Math.random() - 0.5) * 0.25,
          vy: (Math.random() - 0.5) * 0.25 - 0.1,
          size: 0.9 + Math.random() * 1.6,
          color: BEAM_PALETTE[Math.floor(Math.random() * BEAM_PALETTE.length)],
          life: 0,
          lifespan: 700 + Math.random() * 700
        });
      }, isMobile ? 220 : 130);
    }
    function stopHoverEmit() {
      if (hoverEmitTimer) { window.clearInterval(hoverEmitTimer); hoverEmitTimer = null; }
      hoveredEl = null;
    }
    document.addEventListener('mouseover', function (ev) {
      var el = ev.target.closest && ev.target.closest(HOVER_SELECTOR);
      if (el) startHoverEmit(el);
    }, { passive: true });
    document.addEventListener('mouseout', function (ev) {
      var el = ev.target.closest && ev.target.closest(HOVER_SELECTOR);
      if (el === hoveredEl) stopHoverEmit();
    }, { passive: true });

    /* ---------- Sparkle rain (pop-up brillantini ovunque, vita breve) ---------- */
    var sparkles = [];
    var SPARKLE_MAX = isMobile ? 32 : 60;
    var SPARKLE_SPAWN_INTERVAL = isMobile ? 280 : 150; // ms tra spawn (più glitter)
    var lastSparkleSpawn = performance.now();

    function spawnSparkle() {
      if (sparkles.length >= SPARKLE_MAX) return;
      sparkles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        size: 1.0 + Math.random() * 1.8,
        life: 0,
        lifespan: 1100 + Math.random() * 1200, // 1.1–2.3 s
        maxAlpha: 0.55 + Math.random() * 0.35,
        gold: Math.random() > 0.4 // 60% gold, 40% warm-white
      });
    }

    /* ---------- Nebulae (cluster atmosferici di sfondo per profondità) ----------
       Macchie soft di colore pastello disegnate con additive blend (lighter)
       prima delle stelle: aggiungono "vastità cosmica" senza coprire il sito.
       Drift lento + parallasse minima = senso di "molto lontano". */
    var NEBULA_COUNT = isMobile ? 3 : 5;
    var NEBULA_PALETTE = [
      '255, 180, 220',  // pink
      '180, 150, 255',  // violet
      '255, 220, 130',  // gold
      '130, 200, 255',  // cyan
      '180, 255, 200'   // mint
    ];
    var nebulae = [];
    for (var nIdx = 0; nIdx < NEBULA_COUNT; nIdx++) {
      nebulae.push({
        x: Math.random() * width,
        y: Math.random() * height,
        radius: 240 + Math.random() * 280,           // 240–520 px
        color: NEBULA_PALETTE[nIdx % NEBULA_PALETTE.length],
        baseAlpha: 0.005 + Math.random() * 0.012,    // quasi invisibili (0.005–0.017)
        vx: (Math.random() - 0.5) * 0.014,
        vy: (Math.random() - 0.5) * 0.011,
        pulsePhase: Math.random() * Math.PI * 2,
        pulseSpeed: 0.12 + Math.random() * 0.18,
        depth: 0.02 + Math.random() * 0.04           // parallasse minima
      });
    }

    /* ---------- Global dust particles ---------- */
    var DUST_COUNT = isMobile ? 14 : 24;
    var dust = [];
    for (var p = 0; p < DUST_COUNT; p++) {
      dust.push({
        x: Math.random() * width,
        y: Math.random() * height,
        size: 1.4 + Math.random() * 1.8,                // 1.4–3.2 (prima 0.9–2.3)
        vx: (Math.random() - 0.5) * 0.035,
        vy: -0.018 - Math.random() * 0.035,
        life: Math.random(),
        lifespan: 14000 + Math.random() * 12000,
        maxAlpha: 0.4 + Math.random() * 0.25            // 0.4–0.65 (prima 0.18–0.38)
      });
    }

    /* ---------- Scroll parallax ---------- */
    var scrollY = window.scrollY || 0;
    var scrollRaf = 0;
    function onScroll() {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(function () {
        scrollY = window.scrollY || 0;
        scrollRaf = 0;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });

    /* ---------- Resize handler (debounced via RAF) ---------- */
    var resizeRaf = 0;
    window.addEventListener('resize', function () {
      if (resizeRaf) return;
      resizeRaf = requestAnimationFrame(function () {
        resize();
        // Redistribute stars that went out of bounds
        for (var s = 0; s < stars.length; s++) {
          if (stars[s].x > width) stars[s].x = Math.random() * width;
          if (stars[s].y > height) stars[s].y = Math.random() * height;
        }
        resizeRaf = 0;
      });
    }, { passive: true });

    /* ---------- Tab visibility pause ---------- */
    var paused = false;
    document.addEventListener('visibilitychange', function () {
      paused = document.hidden;
      if (!paused) {
        var nowResume = performance.now();
        lastTime = nowResume;
        lastDustTime = nowResume;
        lastSparkleTime = nowResume;
        lastShootingTime = nowResume;
        lastBeamTime = nowResume;
        // Re-allinea i prossimi spawn così non parte raffica al rientro
        nextShootingSpawn = nowResume + (isMobile ? 6000 : 3500);
        nextBeamSpawn = nowResume + (isMobile ? 7000 : 4000);
      }
    });

    /* ---------- Render loop ---------- */
    var targetFps = isMobile ? 30 : 60;
    var frameInterval = 1000 / targetFps;
    var lastTime = performance.now();
    var accumulator = 0;

    function draw(now) {
      var t = now / 1000;
      var wrapH = height + 120;

      ctx.clearRect(0, 0, width, height);

      /* Nebulae + Stars + Shooting stars + Vignette: DISABILITATI per richiesta
         utente (sostituiti da SVG icons simpatici). Il blocco è wrappato in
         `if (false)` per poterlo riattivare facilmente in futuro. */
      if (false) {
      ctx.globalCompositeOperation = 'lighter';
      for (var nbi = 0; nbi < nebulae.length; nbi++) {
        var nb = nebulae[nbi];
        nb.x += nb.vx * 0.9;   // drift molto lento
        nb.y += nb.vy * 0.9;
        // Wrap morbido
        if (nb.x < -nb.radius) nb.x = width + nb.radius;
        else if (nb.x > width + nb.radius) nb.x = -nb.radius;
        if (nb.y < -nb.radius) nb.y = height + nb.radius;
        else if (nb.y > height + nb.radius) nb.y = -nb.radius;
        var nbPulse = 0.85 + 0.15 * Math.sin(t * nb.pulseSpeed + nb.pulsePhase);
        var nbPY = scrollY * nb.depth;
        var nbY = ((nb.y + nbPY) % wrapH) - 60;
        var nbR = nb.radius * nbPulse;
        var nbA = nb.baseAlpha * nbPulse;
        var nbGrad = ctx.createRadialGradient(nb.x, nbY, 0, nb.x, nbY, nbR);
        nbGrad.addColorStop(0,    'rgba(' + nb.color + ', ' + nbA.toFixed(3) + ')');
        nbGrad.addColorStop(0.45, 'rgba(' + nb.color + ', ' + (nbA * 0.45).toFixed(3) + ')');
        nbGrad.addColorStop(1,    'rgba(' + nb.color + ', 0)');
        ctx.fillStyle = nbGrad;
        ctx.beginPath();
        ctx.arc(nb.x, nbY, nbR, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = 'source-over';

      /* Stars — lifecycle: ogni stella nasce, brilla, svanisce e poi respawn
         in una nuova posizione random. Phase iniziale random = alternanza
         naturale (alcune accese mentre altre sono spente). */
      for (var si = 0; si < stars.length; si++) {
        var s = stars[si];
        var elapsed = t - s.spawnTime;
        var cycle = Math.floor(elapsed / s.lifespan);
        if (cycle !== s.lastCycle && s.lastCycle !== -1) {
          // Nuovo ciclo → respawn in posizione random
          s.x = Math.random() * width;
          s.y = Math.random() * height;
        }
        s.lastCycle = cycle;
        var lifeFrac = elapsed / s.lifespan;
        lifeFrac = lifeFrac - Math.floor(lifeFrac); // 0..1
        // Bell curve un po' affilata: 0 → peak (~0.5) → 0
        var lifeCurve = Math.pow(Math.sin(lifeFrac * Math.PI), 1.35);
        // Micro flicker solo vicino al peak (più realistico)
        var flickerAmp = lifeCurve * 0.10;
        var flicker = 1 - flickerAmp + flickerAmp * Math.sin(t * 8 + s.flickerPhase);
        var alpha = s.baseAlpha * lifeCurve * flicker;
        var rgb = s.gold ? STAR_GOLD : STAR_CREAM;
        var starPY = scrollY * s.depth;
        var sy = ((s.y + starPY) % wrapH) - 60;

        // Halo principale: bg sfocate (lontane), hero più nitide
        var haloMul = s.tier === 'hero' ? 4.6 : s.tier === 'mid' ? 3.4 : 3.8;
        var haloR = s.size * haloMul;
        if (alpha > 0.18) {
          var grad = ctx.createRadialGradient(s.x, sy, 0, s.x, sy, haloR);
          var haloAlpha = s.tier === 'hero' ? alpha * 0.55 : alpha * 0.42;
          grad.addColorStop(0, 'rgba(' + rgb + ', ' + haloAlpha.toFixed(3) + ')');
          grad.addColorStop(1, 'rgba(' + rgb + ', 0)');
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(s.x, sy, haloR, 0, Math.PI * 2);
          ctx.fill();
        }

        // Hero stars: 4-point starburst (✦) — punti ☆ stilizzati con spike a croce
        if (s.tier === 'hero' && alpha > 0.30) {
          var spikeLen = s.size * 5.5;
          var spikeAlpha = alpha * 0.80;
          // Spike orizzontale con gradient lineare fading center → tip
          var hg = ctx.createLinearGradient(s.x - spikeLen, sy, s.x + spikeLen, sy);
          hg.addColorStop(0, 'rgba(' + rgb + ', 0)');
          hg.addColorStop(0.5, 'rgba(' + rgb + ', ' + spikeAlpha.toFixed(3) + ')');
          hg.addColorStop(1, 'rgba(' + rgb + ', 0)');
          ctx.strokeStyle = hg;
          ctx.lineWidth = 0.85;
          ctx.beginPath();
          ctx.moveTo(s.x - spikeLen, sy);
          ctx.lineTo(s.x + spikeLen, sy);
          ctx.stroke();
          // Spike verticale
          var vg = ctx.createLinearGradient(s.x, sy - spikeLen, s.x, sy + spikeLen);
          vg.addColorStop(0, 'rgba(' + rgb + ', 0)');
          vg.addColorStop(0.5, 'rgba(' + rgb + ', ' + spikeAlpha.toFixed(3) + ')');
          vg.addColorStop(1, 'rgba(' + rgb + ', 0)');
          ctx.strokeStyle = vg;
          ctx.beginPath();
          ctx.moveTo(s.x, sy - spikeLen);
          ctx.lineTo(s.x, sy + spikeLen);
          ctx.stroke();
        }

        // Core star
        ctx.fillStyle = 'rgba(' + rgb + ', ' + alpha.toFixed(3) + ')';
        ctx.beginPath();
        ctx.arc(s.x, sy, s.size, 0, Math.PI * 2);
        ctx.fill();
      }

      /* Shooting stars: spawn periodico + update + draw (head + trail gradient) */
      if (now >= nextShootingSpawn) {
        spawnShootingStar();
        nextShootingSpawn = now + (isMobile ? (11000 + Math.random() * 7000) : (5000 + Math.random() * 6000));
      }
      for (var ssi = shootingStars.length - 1; ssi >= 0; ssi--) {
        var ss = shootingStars[ssi];
        ss.life += (now - lastShootingTime) / ss.lifespan;
        ss.x += ss.vx;
        ss.y += ss.vy;
        if (ss.life >= 1 || ss.x > width + 80 || ss.y > height + 80) {
          shootingStars.splice(ssi, 1);
          continue;
        }
        // Fade in 0–10%, full 10–75%, fade out 75–100%
        var ssA;
        if (ss.life < 0.10) ssA = ss.life / 0.10;
        else if (ss.life < 0.75) ssA = 1;
        else ssA = (1 - ss.life) / 0.25;
        var speedNorm = Math.sqrt(ss.vx * ss.vx + ss.vy * ss.vy);
        var tailX = ss.x - (ss.vx / speedNorm) * ss.trailLen;
        var tailY = ss.y - (ss.vy / speedNorm) * ss.trailLen;
        var ssRgb = ss.gold ? STAR_GOLD : STAR_CREAM;
        // Trail
        var ssGrad = ctx.createLinearGradient(ss.x, ss.y, tailX, tailY);
        ssGrad.addColorStop(0, 'rgba(' + ssRgb + ', ' + (0.95 * ssA).toFixed(3) + ')');
        ssGrad.addColorStop(1, 'rgba(' + ssRgb + ', 0)');
        ctx.strokeStyle = ssGrad;
        ctx.lineWidth = 1.7;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(ss.x, ss.y);
        ctx.stroke();
        // Head halo
        var ssHaloR = 7;
        var ssHalo = ctx.createRadialGradient(ss.x, ss.y, 0, ss.x, ss.y, ssHaloR);
        ssHalo.addColorStop(0, 'rgba(' + ssRgb + ', ' + (0.85 * ssA).toFixed(3) + ')');
        ssHalo.addColorStop(1, 'rgba(' + ssRgb + ', 0)');
        ctx.fillStyle = ssHalo;
        ctx.beginPath();
        ctx.arc(ss.x, ss.y, ssHaloR, 0, Math.PI * 2);
        ctx.fill();
        // Head core
        ctx.fillStyle = 'rgba(' + ssRgb + ', ' + (0.98 * ssA).toFixed(3) + ')';
        ctx.beginPath();
        ctx.arc(ss.x, ss.y, 1.8, 0, Math.PI * 2);
        ctx.fill();
      }
      lastShootingTime = now;
      } /* end disabled celestial — riprende sotto magic beams + sparkles + dust */

      /* Magic beams: spawn periodico + update + draw + emit brillantini lungo Bezier */
      if (now >= nextBeamSpawn) {
        spawnBeam();
        nextBeamSpawn = now + (isMobile ? (9000 + Math.random() * 8000) : (5500 + Math.random() * 6500));
      }
      var beamDelta = Math.min(now - lastBeamTime, 80);
      lastBeamTime = now;
      for (var bmi = beams.length - 1; bmi >= 0; bmi--) {
        var bm = beams[bmi];
        bm.age += beamDelta;
        var bmt = bm.age / bm.lifespan;
        if (bmt >= 1) { beams.splice(bmi, 1); continue; }
        var bp = bezierPoint(bm, bmt);
        // Emit sparkles ~ogni 35ms desktop / 65ms mobile
        var EMIT_INTERVAL = isMobile ? 65 : 35;
        if (now - bm.lastEmit > EMIT_INTERVAL) {
          bm.lastEmit = now;
          var emitN = isMobile ? 2 : 4;
          for (var ei = 0; ei < emitN; ei++) {
            var jitter = 16;
            beamSparkles.push({
              x: bp[0] + (Math.random() - 0.5) * jitter,
              y: bp[1] + (Math.random() - 0.5) * jitter,
              vx: (Math.random() - 0.5) * 0.35,
              vy: (Math.random() - 0.5) * 0.35,
              size: 1.2 + Math.random() * 2.2,
              color: BEAM_PALETTE[Math.floor(Math.random() * BEAM_PALETTE.length)],
              life: 0,
              lifespan: 900 + Math.random() * 1000
            });
          }
        }
        // Beam head — radial bianco→colore palette ciclato
        var headColor = BEAM_PALETTE[(bm.hueIndex + Math.floor(bm.age / 120)) % BEAM_PALETTE.length];
        // Fade in 0–6%, full 6–88%, fade out 88–100%
        var bmA;
        if (bmt < 0.06) bmA = bmt / 0.06;
        else if (bmt < 0.88) bmA = 1;
        else bmA = (1 - bmt) / 0.12;
        var headR = 16;
        var headGrad = ctx.createRadialGradient(bp[0], bp[1], 0, bp[0], bp[1], headR);
        headGrad.addColorStop(0, 'rgba(255, 255, 255, ' + (0.95 * bmA).toFixed(3) + ')');
        headGrad.addColorStop(0.35, 'rgba(' + headColor + ', ' + (0.75 * bmA).toFixed(3) + ')');
        headGrad.addColorStop(1, 'rgba(' + headColor + ', 0)');
        ctx.fillStyle = headGrad;
        ctx.beginPath();
        ctx.arc(bp[0], bp[1], headR, 0, Math.PI * 2);
        ctx.fill();
      }

      /* Beam sparkles + click/hover sparkles (buffer condiviso, render unificato) */
      for (var bxi = beamSparkles.length - 1; bxi >= 0; bxi--) {
        var bx = beamSparkles[bxi];
        bx.life += beamDelta / bx.lifespan;
        if (bx.life >= 1) { beamSparkles.splice(bxi, 1); continue; }
        bx.x += bx.vx * beamDelta * 0.06;
        bx.y += bx.vy * beamDelta * 0.06;
        // Fade in 0-15%, full 15-65%, fade out 65-100%
        var bxA;
        if (bx.life < 0.15) bxA = bx.life / 0.15;
        else if (bx.life < 0.65) bxA = 1;
        else bxA = (1 - bx.life) / 0.35;
        // Halo colorato
        var bxHaloR = bx.size * 4.2;
        var bxGrad = ctx.createRadialGradient(bx.x, bx.y, 0, bx.x, bx.y, bxHaloR);
        bxGrad.addColorStop(0, 'rgba(' + bx.color + ', ' + (bxA * 0.60).toFixed(3) + ')');
        bxGrad.addColorStop(1, 'rgba(' + bx.color + ', 0)');
        ctx.fillStyle = bxGrad;
        ctx.beginPath();
        ctx.arc(bx.x, bx.y, bxHaloR, 0, Math.PI * 2);
        ctx.fill();
        // Core white brillante
        ctx.fillStyle = 'rgba(255, 255, 255, ' + (bxA * 0.95).toFixed(3) + ')';
        ctx.beginPath();
        ctx.arc(bx.x, bx.y, bx.size, 0, Math.PI * 2);
        ctx.fill();
      }

      /* Sparkle rain: spawn + update + draw */
      if (now - lastSparkleSpawn > SPARKLE_SPAWN_INTERVAL) {
        spawnSparkle();
        // Burst piccolo: ogni tanto spawn doppio (ritmo più vivo)
        if (Math.random() > 0.72) spawnSparkle();
        lastSparkleSpawn = now;
      }
      var sparkleDelta = Math.min(now - lastSparkleTime, 80);
      lastSparkleTime = now;
      for (var spi = sparkles.length - 1; spi >= 0; spi--) {
        var sp = sparkles[spi];
        sp.life += sparkleDelta / sp.lifespan;
        if (sp.life >= 1) {
          sparkles.splice(spi, 1);
          continue;
        }
        // Fade in rapido 0-20%, stay 20-70%, fade out 70-100%
        var spAlpha;
        if (sp.life < 0.2) spAlpha = sp.maxAlpha * (sp.life / 0.2);
        else if (sp.life < 0.7) spAlpha = sp.maxAlpha;
        else spAlpha = sp.maxAlpha * (1 - (sp.life - 0.7) / 0.3);
        // Scale pulse: cresce in life 0-50%, decresce 50-100%
        var spScale = sp.life < 0.5 ? (sp.life / 0.5) : (1 - (sp.life - 0.5) / 0.5);
        spScale = 0.5 + spScale * 0.7; // 0.5-1.2 range
        var spRgb = sp.gold ? STAR_CREAM : '255, 248, 220';
        // Core bright
        ctx.fillStyle = 'rgba(' + spRgb + ', ' + spAlpha.toFixed(3) + ')';
        ctx.beginPath();
        ctx.arc(sp.x, sp.y, sp.size * spScale, 0, Math.PI * 2);
        ctx.fill();
        // Halo
        var spHaloR = sp.size * spScale * 3.5;
        var spGrad = ctx.createRadialGradient(sp.x, sp.y, 0, sp.x, sp.y, spHaloR);
        spGrad.addColorStop(0, 'rgba(' + spRgb + ', ' + (spAlpha * 0.5).toFixed(3) + ')');
        spGrad.addColorStop(1, 'rgba(' + spRgb + ', 0)');
        ctx.fillStyle = spGrad;
        ctx.beginPath();
        ctx.arc(sp.x, sp.y, spHaloR, 0, Math.PI * 2);
        ctx.fill();
      }

      /* Dust particles: update + draw */
      var deltaMs = Math.min(now - lastDustTime, 80); // cap per perf
      lastDustTime = now;
      for (var di = 0; di < dust.length; di++) {
        var d = dust[di];
        d.life += deltaMs / d.lifespan;
        if (d.life >= 1) {
          // Respawn dal basso random x
          d.x = Math.random() * width;
          d.y = height + 8;
          d.life = 0;
          d.maxAlpha = 0.18 + Math.random() * 0.2;
        }
        d.x += d.vx * deltaMs * 0.06;
        d.y += d.vy * deltaMs * 0.06;

        // Wrap orizzontale
        if (d.x < -20) d.x = width + 20;
        else if (d.x > width + 20) d.x = -20;

        // Ciclo di vita: fade-in-out via sinusoide
        var lifeBell = Math.sin(d.life * Math.PI);
        var alpha2 = d.maxAlpha * lifeBell;
        if (alpha2 > 0.015) {
          var dustR = d.size * 3.2;
          var dgrad = ctx.createRadialGradient(d.x, d.y, 0, d.x, d.y, dustR);
          dgrad.addColorStop(0, 'rgba(' + DUST_CORE + ', ' + alpha2.toFixed(3) + ')');
          dgrad.addColorStop(0.55, 'rgba(' + DUST_HALO + ', ' + (alpha2 * 0.55).toFixed(3) + ')');
          dgrad.addColorStop(1, 'rgba(' + DUST_HALO + ', 0)');
          ctx.fillStyle = dgrad;
          ctx.beginPath();
          ctx.arc(d.x, d.y, dustR, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      /* Vignette finale — DISABILITATA (toglieva colore al sito). */
      if (false) {
      var vR = Math.max(width, height) * 0.88;
      var vGrad = ctx.createRadialGradient(
        width * 0.5, height * 0.5, vR * 0.42,
        width * 0.5, height * 0.5, vR
      );
      vGrad.addColorStop(0,    'rgba(30, 15, 8, 0)');
      vGrad.addColorStop(0.55, 'rgba(30, 15, 8, 0.04)');
      vGrad.addColorStop(1,    'rgba(30, 15, 8, 0.22)');
      ctx.fillStyle = vGrad;
      ctx.fillRect(0, 0, width, height);
      } /* end if (false) — sezione celeste disabilitata */
    }

    var lastDustTime = performance.now();
    var lastSparkleTime = performance.now();
    var lastShootingTime = performance.now();
    var lastBeamTime = performance.now();

    function loop(now) {
      if (paused) {
        requestAnimationFrame(loop);
        return;
      }
      var delta = now - lastTime;
      lastTime = now;
      accumulator += delta;
      if (accumulator >= frameInterval) {
        draw(now);
        accumulator = accumulator % frameInterval;
      }
      requestAnimationFrame(loop);
    }

    // Mark initialized → CSS transition opacity 0 → 1
    canvas.setAttribute('data-world-initialized', '');

    if (reducedMotion) {
      // 1 frame statico e stop
      draw(performance.now());
    } else {
      requestAnimationFrame(loop);
    }
  }

  /* ==========================================================================
     E2 — Floating creatures scheduler
     Orchestra 5 creature (butterfly, firefly, boat, bird, unicorn) in scena.
     Commerce guardrail: skip su product/collection/list-collections/cart.
     ========================================================================== */
  function initCreatures() {
    if (reducedMotion) return;

    // Template guardrail — leggi data-template dal <main>
    var mainEl = document.getElementById('MainContent');
    var template = mainEl ? (mainEl.getAttribute('data-template') || '') : '';
    var COMMERCE = ['product', 'collection', 'list-collections', 'cart'];
    for (var t = 0; t < COMMERCE.length; t++) {
      if (template.indexOf(COMMERCE[t]) === 0) return;
    }

    var container = document.querySelector('.ew-creatures');
    if (!container) return;
    var creatures = container.querySelectorAll('.ew-creature');
    if (!creatures.length) return;

    // Timing per le creature ambientali (farfalla/lucciola/barca/uccellino)
    var MIN_WAIT = isMobile ? 2500 : 1100;
    var MAX_WAIT = isMobile ? 6000 : 3200;
    var MAX_CONCURRENT = isMobile ? 2 : 3;

    // Durata drift per tipo (+ margine prima di liberare slot)
    var DRIFT_MS = {
      'butterfly': 22500,
      'firefly':   19500,
      'boat':      26500,
      'bird':      16500,
      'astronaut': 24500,
      'rocket':    12500,
      'car':       18500,
      'plane':     13500,
      'bicycle':   24500,
      'motorbike': 15500,
      'lipstick':  21500,
      'palette':   22500
    };

    var activeCount = 0;

    function playRandom() {
      if (activeCount < MAX_CONCURRENT) {
        var available = [];
        for (var i = 0; i < creatures.length; i++) {
          var el = creatures[i];
          var cs = window.getComputedStyle(el);
          if (cs.display === 'none') continue;
          if (el.classList.contains('is-active')) continue;
          available.push(el);
        }

        if (available.length) {
          var pick = available[Math.floor(Math.random() * available.length)];
          var kind = pick.getAttribute('data-creature');
          pick.style.top = (12 + Math.random() * 55) + '%';
          pick.classList.add('is-active');
          activeCount++;
          window.setTimeout(function () {
            pick.classList.remove('is-active');
            activeCount--;
          }, (DRIFT_MS[kind] || 20000) + 500);
        }
      }

      var wait = MIN_WAIT + Math.random() * (MAX_WAIT - MIN_WAIT);
      window.setTimeout(playRandom, wait);
    }

    // Prima creatura a +2 s
    window.setTimeout(playRandom, 2000);

    // Pause su tab hidden
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        container.setAttribute('data-creatures-paused', '');
      } else {
        container.removeAttribute('data-creatures-paused');
      }
    });
  }

  function bootstrap() {
    init();
    initCreatures();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
